import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import Modal from './Modal';
import axiosInstance from '../../utils/axiosInstance';
import { Shield } from 'lucide-react';

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    message?: string;
    showCreditOption?: boolean;
    onBuyCredit?: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ 
    isOpen, 
    onClose, 
    message,
    showCreditOption = true,
    onBuyCredit 
}) => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const handleUpgrade = () => {
        navigate(`/${language}/profile`);
        onClose();
    };
    
    const handleBuyCredit = async () => {
        if (onBuyCredit) {
            onBuyCredit();
            return;
        }
        
        setIsLoading(true);
        try {
            const response = await axiosInstance.post('/create-credit-checkout-session');
            
            if (response.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="p-6 max-w-md w-full bg-[var(--bg-secondary)] rounded-xl">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
                    {t('subscription.creditsOrSubscription')}
                </h2>
                <p className="text-[var(--text-secondary)] mb-4">
                    {message || t('subscription.creditsOrSubscriptionMessage')}
                </p>
                <div className="flex items-center gap-2 mb-6 text-sm">
                    <Shield className="w-4 h-4 text-success" />
                    <Link 
                        to={`/${language}/money-back-guarantee`} 
                        className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] underline"
                        onClick={onClose}
                    >
                        {t('pricing.moneyBackGuarantee')}
                    </Link>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                        {t('common.cancel')}
                    </button>
                    
                    {showCreditOption && (
                        <button
                            onClick={handleBuyCredit}
                            disabled={isLoading}
                            className="px-6 py-2 bg-[var(--bg-primary)] border border-[var(--brand-primary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-secondary)] transition-all duration-300 disabled:opacity-50"
                        >
                            {isLoading ? t('common.loading') : t('pricing.buyCreditAction')}
                        </button>
                    )}
                    
                    <button
                        onClick={handleUpgrade}
                        className="px-6 py-2 bg-[var(--brand-primary)] text-[var(--text-button)] rounded-lg hover:bg-[var(--brand-hover)] transition-all duration-300"
                    >
                        {t('subscription.upgradeAction')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SubscriptionModal;